import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const FONTS_URL =
  "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap";

function useFonts() {
  useEffect(() => {
    if (document.querySelector('link[href*="Instrument+Serif"]')) return;
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = FONTS_URL;
    document.head.appendChild(l);
  }, []);
}

const T = {
  g700: "#155c36", g600: "#1b7a47", g500: "#1e9254", g400: "#28b566",
  g200: "#a7d9bc", g100: "#d6f0e3", g50: "#eef8f3",
  n900: "#0f1117", n800: "#1c2130", n700: "#2d3549", n600: "#4a5268",
  n400: "#8a93a8", n300: "#b0b8cc", n200: "#d1d5e0", n150: "#e4e7ef",
  n100: "#eef0f5", n50: "#f7f8fa", white: "#ffffff",
  amber: "#b45309", amberBg: "#fffbeb", amberBorder: "#fde68a",
  blue: "#2563eb", blueBg: "#eff6ff", blueBorder: "#bfdbfe",
  purple: "#7c3aed", purpleBg: "#fdf4ff", purpleBorder: "#e9d5ff",
  serif: "'Instrument Serif', Georgia, serif",
  sans: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
};

const Icon = ({ name, size = 20, color = "currentColor", strokeWidth = 1.6 }) => {
  const paths = {
    ArrowLeft:     <><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></>,
    ArrowRight:    <><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></>,
    MapPin:        <><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z"/><circle cx="12" cy="10" r="3"/></>,
    Briefcase:     <><rect width="20" height="14" x="2" y="7" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></>,
    Code:          <><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></>,
    ShieldCheck:   <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></>,
    TrendingUp:    <><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></>,
    Users:         <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>,
    Zap:           <><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></>,
    Heart:         <><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></>,
    Mail:          <><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></>,
    Clock:         <><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></>,
    CheckCircle:   <><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></>,
    Sparkles:      <><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3z"/></>,
    ChevronDown:   <><path d="m6 9 6 6 6-6"/></>,
    ChevronUp:     <><path d="m18 15-6-6-6 6"/></>,
    ExternalLink:  <><path d="M15 3h6v6"/><path d="M10 14 21 3"/><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/></>,
    Star:          <><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></>,
    Globe:         <><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></>,
    Wrench:        <><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></>,
    Megaphone:     <><path d="m3 11 19-9-9 19-2-8-8-2z"/></>,
    BarChart:      <><line x1="12" x2="12" y1="20" y2="10"/><line x1="18" x2="18" y1="20" y2="4"/><line x1="6" x2="6" y1="20" y2="16"/></>,
  };
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      {paths[name]}
    </svg>
  );
};

/* ── Data ── */
const ROLES = [
  {
    id: 1,
    title: "Senior Full-Stack Engineer",
    dept: "Engineering",
    deptIcon: "Code",
    deptColor: { color: T.blue, bg: T.blueBg, border: T.blueBorder },
    type: "Full-time",
    location: "Nairobi (hybrid)",
    summary: "You will own large pieces of the JiraniApp product end-to-end -- from the React frontend clients use to book a service, to the Supabase backend that runs the verification, payment, and scheduling logic. We are a small engineering team, which means your decisions actually ship.",
    about: "JiraniApp runs on React, Supabase (Postgres + Auth), Tailwind CSS, and integrates directly with M-Pesa's Daraja API. We are a mobile-first product targeting mid-range Android devices on variable connections. Speed, reliability, and offline-graceful behaviour matter here in ways they do not on a broadband-only product.",
    responsibilities: [
      "Build and ship product features across the full stack -- React frontend, Supabase backend, and API integrations",
      "Own the M-Pesa payment rail and keep it robust as transaction volume grows",
      "Improve app performance on low-end Android devices and slow connections",
      "Work directly with the founders on architecture decisions -- no layers of management between you and the problem",
      "Write clean, reviewable code and hold the bar for the team doing the same",
    ],
    requirements: [
      "3+ years of professional software engineering experience",
      "Strong React and modern JavaScript -- you can read our codebase and be productive in the first week",
      "Comfortable with Postgres, REST APIs, and working directly with a BaaS like Supabase or Firebase",
      "You have shipped something real -- something people actually use",
      "Based in Nairobi or able to work Nairobi hours reliably",
    ],
    nice: [
      "Experience with M-Pesa Daraja API or other mobile money integrations",
      "Background building for low-bandwidth or low-memory environments",
      "You have worked at or founded an early-stage company before",
    ],
  },
  {
    id: 2,
    title: "Trust and Safety Lead",
    dept: "Operations",
    deptIcon: "ShieldCheck",
    deptColor: { color: T.g600, bg: T.g50, border: T.g100 },
    type: "Full-time",
    location: "Nairobi (on-site)",
    summary: "Every professional who joins JiraniApp passes through this role before going live. You will own verification, disputes, and the standards that determine who belongs on the platform and who does not. This is not a moderation job -- it is the operational backbone of why clients trust us.",
    about: "Trust and Safety at JiraniApp is not a reactive function. It is what makes the product work. The team reviews professional applications, investigates disputes from both clients and Pros, and sets the standards for what good looks like. It requires good judgement, consistency, and the ability to make difficult calls under time pressure.",
    responsibilities: [
      "Review and make decisions on all incoming JiraniPro applications -- identity verification, credentials, background flags",
      "Manage the end-to-end dispute resolution process for both clients and professionals",
      "Build and maintain the internal playbooks, rubrics, and escalation logic for edge cases",
      "Track and report on trust metrics: verification times, dispute rates, resolution outcomes, repeat offenders",
      "Work with the engineering team to improve the tooling that supports this work",
    ],
    requirements: [
      "Experience in trust and safety, community operations, or a related function -- marketplace experience preferred",
      "Excellent written and spoken Swahili and English -- you will communicate with professionals and clients across Kenya",
      "Strong decision-making under ambiguity -- you can reach a fair conclusion quickly without a perfect playbook",
      "High attention to detail and comfort with documentation",
      "Nairobi-based and available on-site",
    ],
    nice: [
      "Background in a gig economy, marketplace, or platform company",
      "Experience setting up trust systems from scratch, not just inheriting them",
      "Familiarity with Kenyan consumer protection law or informal sector dynamics",
    ],
  },
  {
    id: 3,
    title: "Product Manager",
    dept: "Product",
    deptIcon: "Sparkles",
    deptColor: { color: T.purple, bg: T.purpleBg, border: T.purpleBorder },
    type: "Full-time",
    location: "Nairobi (hybrid)",
    summary: "You will own the product roadmap and work directly with the engineering team and founders to decide what gets built, why, and when. JiraniApp is early -- this role is for someone who wants to shape a product from the ground up, not optimise an existing one.",
    about: "Product at JiraniApp means working in two directions at once: the client experience (search, booking, payment, review) and the professional experience (onboarding, dashboard, earnings, scheduling). Both matter equally. The right candidate understands marketplace dynamics, has deep empathy for users who are not like them, and can translate a messy problem into a clean, testable decision.",
    responsibilities: [
      "Own the product roadmap -- prioritise ruthlessly, say no clearly, and explain your reasoning",
      "Conduct regular user research with both clients and JiraniPros across different Kenyan cities",
      "Write clear product specs and work closely with engineering through build, review, and launch",
      "Track key product metrics and use them to drive decisions, not to decorate slide decks",
      "Be the person who understands the product more deeply than anyone who is not an engineer or founder",
    ],
    requirements: [
      "3+ years of product management experience, ideally at a marketplace, platform, or consumer app",
      "Proven track record of shipping features that measurably improved user outcomes",
      "Strong written communication -- your specs should need one round of clarification, not five",
      "Comfort with data: SQL or the willingness to learn it, and the ability to define and interpret metrics",
      "Nairobi-based or able to work in person regularly",
    ],
    nice: [
      "Experience building for African markets, particularly around mobile-first and M-Pesa-native flows",
      "Background in marketplace or two-sided platform products",
      "You have worked at a startup at an early enough stage that your title did not match your scope",
    ],
  },
  {
    id: 4,
    title: "City Growth Manager",
    dept: "Growth",
    deptIcon: "TrendingUp",
    deptColor: { color: T.amber, bg: T.amberBg, border: T.amberBorder },
    type: "Full-time",
    location: "Mombasa or Kisumu",
    summary: "You will own supply and demand in a specific city -- recruiting new JiraniPros, working with local partners, and making sure that when a client searches for a plumber in Nyali or a cleaner in Kondele, there is someone good available. This is a ground-level, relationship-intensive role.",
    about: "Marketplace growth is a supply problem as much as it is a demand problem. The City Growth Manager is responsible for making sure the right professionals are on the platform in their city, that they are active and booking consistently, and that client demand is being met. It requires local knowledge, persistence, and the ability to build trust with people in the informal sector.",
    responsibilities: [
      "Recruit and onboard new JiraniPros in your city -- identify, approach, explain, and support them through verification",
      "Build relationships with trade associations, TVET institutions, and community networks to create ongoing talent pipelines",
      "Monitor supply and demand in your city and flag gaps to the product and ops teams",
      "Run local events, informal meetups, and support sessions to keep JiraniPros engaged and informed",
      "Report weekly on key city metrics: new Pro activations, booking rates, supply-demand coverage",
    ],
    requirements: [
      "Strong local knowledge of your city and its informal services ecosystem",
      "Demonstrated ability to build trust with strangers quickly and professionally",
      "Self-directed and comfortable working without close oversight from a central office",
      "Excellent communication in English and Swahili -- and ideally in additional local languages",
      "Based in Mombasa or Kisumu (this is not a remote role)",
    ],
    nice: [
      "Background in field sales, community organising, or on-the-ground operations",
      "Existing network in local trades or service businesses",
      "Experience with data tracking tools -- even basic spreadsheet discipline goes a long way",
    ],
  },
  {
    id: 5,
    title: "Customer Experience Associate",
    dept: "Operations",
    deptIcon: "Users",
    deptColor: { color: T.g600, bg: T.g50, border: T.g100 },
    type: "Full-time",
    location: "Nairobi (on-site)",
    summary: "You will be the first person clients and JiraniPros speak to when something goes wrong -- or when they just need help. This role is not a call centre job. It is the frontline of how JiraniApp keeps trust after a booking goes badly, and your quality of judgement matters as much as your speed.",
    about: "Great customer experience at a marketplace is not about scripts. It is about reaching a fair resolution quickly, communicating clearly under pressure, and knowing when to escalate. You will handle inbound queries via the app, resolve straightforward disputes, support professionals through onboarding questions, and keep the Trust and Safety Lead informed on patterns you are seeing.",
    responsibilities: [
      "Respond to client and JiraniPro queries through the in-app messaging system -- promptly and clearly",
      "Resolve straightforward disputes and escalate complex cases to the Trust and Safety Lead with full context",
      "Support JiraniPros through the onboarding process when they are stuck",
      "Keep records of recurring issues and flag them as product or process feedback",
      "Maintain a response time and resolution quality standard that the team can be proud of",
    ],
    requirements: [
      "Excellent written communication in English and Swahili -- you can be firm, warm, and clear at the same time",
      "High empathy and patience, particularly under pressure or when dealing with upset users",
      "Organised and consistent -- you track open issues and do not let things fall through",
      "Nairobi-based and available on-site",
      "Prior customer-facing experience in any sector is preferred but not required if the rest is there",
    ],
    nice: [
      "Experience at a marketplace, logistics company, or consumer app",
      "Familiarity with dispute resolution or complaint handling processes",
      "Ability to communicate in additional Kenyan languages beyond Swahili and English",
    ],
  },
];

const PERKS = [
  { icon: "MapPin",   title: "Based in Nairobi",           desc: "We are a Nairobi-first team. Most roles are hybrid or on-site. We believe the best work on this problem happens close to the people it affects." },
  { icon: "Zap",      title: "Real ownership early",       desc: "We are small. Whatever you build, you will see it ship. There is no six-month onboarding runway before your first contribution matters." },
  { icon: "Heart",    title: "Work that is not abstract",  desc: "The JiraniPros and clients on this platform are real people with real stakes. You will feel that in the work every day." },
  { icon: "Globe",    title: "Competitive for early-stage", desc: "We pay market-competitive salaries for a seed-stage company. We are transparent about our ranges and do not negotiate in bad faith." },
  { icon: "Star",     title: "Equity that means something", desc: "We offer equity to all full-time hires. We will explain the cap table clearly and answer every question you have about what your stake actually represents." },
  { icon: "Clock",    title: "Flexible where it matters",  desc: "We care about output and reliability, not time logged. If you need flexibility around your schedule, we will find a way to make it work." },
];

/* ── Dept filter pill ── */
const ALL_DEPTS = ["All", "Engineering", "Product", "Operations", "Growth"];

/* ── Role card ── */
function RoleCard({ role, onApply }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div style={{
      border: `1.5px solid ${expanded ? T.g200 : T.n150}`,
      borderRadius: 16, overflow: "hidden",
      background: T.white,
      transition: "border-color .2s",
    }}>
      {/* Header */}
      <button
        onClick={() => setExpanded(o => !o)}
        style={{
          width: "100%", textAlign: "left", background: "none", border: "none",
          cursor: "pointer", padding: "24px 24px 20px", fontFamily: T.sans,
          display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16,
        }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Dept badge + meta */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 5,
              fontSize: 11, fontWeight: 600, letterSpacing: "0.04em",
              color: role.deptColor.color, background: role.deptColor.bg,
              border: `1px solid ${role.deptColor.border}`,
              borderRadius: 99, padding: "3px 10px",
            }}>
              <Icon name={role.deptIcon} size={11} color={role.deptColor.color} strokeWidth={2} />
              {role.dept}
            </span>
            <span style={{ fontSize: 12, color: T.n400, display: "flex", alignItems: "center", gap: 4 }}>
              <Icon name="MapPin" size={11} color={T.n400} strokeWidth={2} />
              {role.location}
            </span>
            <span style={{ fontSize: 12, color: T.n400, display: "flex", alignItems: "center", gap: 4 }}>
              <Icon name="Briefcase" size={11} color={T.n400} strokeWidth={2} />
              {role.type}
            </span>
          </div>
          <div style={{ fontFamily: T.serif, fontSize: "clamp(17px,2.5vw,21px)", fontWeight: 400, color: T.n900, letterSpacing: "-0.02em", lineHeight: 1.25, marginBottom: 8 }}>
            {role.title}
          </div>
          <p style={{ fontSize: 14, color: T.n600, lineHeight: 1.7, margin: 0 }}>{role.summary}</p>
        </div>
        <span style={{
          width: 30, height: 30, borderRadius: "50%", flexShrink: 0, marginTop: 2,
          border: `1px solid ${expanded ? T.g500 : T.n200}`,
          background: expanded ? T.g500 : "transparent",
          color: expanded ? T.white : T.n400,
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "all .2s",
        }}>
          <Icon name={expanded ? "ChevronUp" : "ChevronDown"} size={15} strokeWidth={2.5} color={expanded ? T.white : T.n400} />
        </span>
      </button>

      {/* Expanded body */}
      <div style={{
        maxHeight: expanded ? 2000 : 0, overflow: "hidden",
        transition: "max-height .4s cubic-bezier(.4,0,.2,1)",
      }}>
        <div style={{ padding: "0 24px 28px", borderTop: `1px solid ${T.n100}`, paddingTop: 24 }}>

          {/* About the context */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.n400, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10 }}>The context</div>
            <p style={{ fontSize: 14, color: T.n600, lineHeight: 1.8, margin: 0 }}>{role.about}</p>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 24, marginBottom: 24 }}>
            {/* Responsibilities */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.n400, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 12 }}>What you will do</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {role.responsibilities.map((r, i) => (
                  <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                    <div style={{ width: 18, height: 18, borderRadius: "50%", background: T.g50, border: `1px solid ${T.g100}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
                      <Icon name="CheckCircle" size={10} color={T.g500} strokeWidth={2.5} />
                    </div>
                    <span style={{ fontSize: 13, color: T.n700, lineHeight: 1.7 }}>{r}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Requirements */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.n400, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 12 }}>What we are looking for</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {role.requirements.map((r, i) => (
                  <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                    <div style={{ width: 6, height: 6, borderRadius: "50%", background: T.n300, flexShrink: 0, marginTop: 7 }} />
                    <span style={{ fontSize: 13, color: T.n700, lineHeight: 1.7 }}>{r}</span>
                  </div>
                ))}
              </div>

              {role.nice && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: T.n400, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10 }}>Good to have</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {role.nice.map((n, i) => (
                      <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                        <div style={{ width: 6, height: 6, borderRadius: "50%", background: T.g200, flexShrink: 0, marginTop: 7 }} />
                        <span style={{ fontSize: 13, color: T.n600, lineHeight: 1.7 }}>{n}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Apply CTA */}
          <div style={{ paddingTop: 20, borderTop: `1px solid ${T.n100}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
            <p style={{ fontSize: 13, color: T.n400, margin: 0, lineHeight: 1.6, maxWidth: 360 }}>
              Send your CV and a short note about why this role, to{" "}
              <a href={`mailto:jobs@jiraniapp.co.ke?subject=${encodeURIComponent("Application: " + role.title)}`} style={{ color: T.g600, textDecoration: "none", fontWeight: 600 }}>
                jobs@jiraniapp.co.ke
              </a>
            </p>
            <a
              href={`mailto:jobs@jiraniapp.co.ke?subject=${encodeURIComponent("Application: " + role.title)}&body=${encodeURIComponent("Hi Jirani team,\n\nI am applying for the " + role.title + " role.\n\n")}`}
              style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                background: T.g500, color: T.white, border: "none",
                padding: "12px 22px", borderRadius: 10, fontSize: 14, fontWeight: 600,
                cursor: "pointer", fontFamily: T.sans, letterSpacing: "0.01em",
                textDecoration: "none", flexShrink: 0,
              }}
              onMouseEnter={e => e.currentTarget.style.background = T.g600}
              onMouseLeave={e => e.currentTarget.style.background = T.g500}>
              Apply for this role
              <Icon name="Mail" size={14} color={T.white} strokeWidth={2} />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Perk card ── */
function PerkCard({ icon, title, desc }) {
  return (
    <div style={{ padding: "22px 0", borderBottom: `1px solid ${T.n100}`, display: "flex", gap: 16, alignItems: "flex-start" }}>
      <div style={{ width: 38, height: 38, borderRadius: 11, background: T.g50, border: `1px solid ${T.g100}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon name={icon} size={17} color={T.g500} strokeWidth={1.7} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontFamily: T.sans, fontSize: 14, fontWeight: 600, color: T.n900, marginBottom: 5, letterSpacing: "-0.01em" }}>{title}</div>
        <p style={{ fontFamily: T.sans, fontSize: 14, color: T.n600, lineHeight: 1.7, margin: 0 }}>{desc}</p>
      </div>
    </div>
  );
}

/* ══ MAIN PAGE ══ */
export default function CareersPage() {
  useFonts();
  const navigate = useNavigate();

  const [activeDept, setActiveDept] = useState("All");

  const filtered = ROLES.filter(r => activeDept === "All" || r.dept === activeDept);

  return (
    <div style={{ fontFamily: T.sans, background: T.white, minHeight: "100vh", color: T.n900 }}>
      <style>{`
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { display: none; }
      `}</style>

      {/* ── Nav ── */}
      <div style={{ padding: "16px 24px", borderBottom: `1px solid ${T.n100}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <button onClick={() => navigate(-1)} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: T.n400, fontSize: 13, fontFamily: T.sans, padding: 0 }}
          onMouseEnter={e => e.currentTarget.style.color = T.n800}
          onMouseLeave={e => e.currentTarget.style.color = T.n400}>
          <Icon name="ArrowLeft" size={14} strokeWidth={2} />
          Back
        </button>
        <a href="mailto:jobs@jiraniapp.co.ke" style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: `1px solid ${T.n200}`, cursor: "pointer", color: T.n600, fontSize: 13, fontFamily: T.sans, padding: "8px 16px", borderRadius: 8, fontWeight: 500, textDecoration: "none" }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = T.n400; e.currentTarget.style.color = T.n900; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = T.n200; e.currentTarget.style.color = T.n600; }}>
          <Icon name="Mail" size={13} strokeWidth={2} />
          jobs@jiraniapp.co.ke
        </a>
      </div>

      {/* ══ HERO ══ */}
      <div style={{ padding: "clamp(56px,8vw,104px) 20px clamp(48px,6vw,80px)", borderBottom: `1px solid ${T.n100}` }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, color: T.g600, fontSize: 12, fontWeight: 600, letterSpacing: "0.09em", textTransform: "uppercase", marginBottom: 24 }}>
            <Icon name="Sparkles" size={14} color={T.g500} strokeWidth={2.2} />
            Careers at JiraniApp
          </div>
          <h1 style={{ fontFamily: T.serif, fontSize: "clamp(40px,7.5vw,70px)", fontWeight: 400, margin: "0 0 24px", lineHeight: 1.04, color: T.n900, letterSpacing: "-0.03em", maxWidth: 640 }}>
            Build something that matters<br/>
            <em style={{ fontStyle: "italic", color: T.g600 }}>for people who are right here</em>
          </h1>
          <p style={{ fontSize: "clamp(15px,2vw,17px)", color: T.n600, lineHeight: 1.8, margin: "0 0 36px", maxWidth: 520 }}>
            JiraniApp is a small, early-stage team building the trust layer that home services in Kenya have never had.
            We are hiring people who care about the problem more than the prestige of the company.
          </p>

          {/* Role count callout */}
          <div style={{ display: "inline-flex", alignItems: "center", gap: 10, padding: "12px 18px", borderRadius: 12, background: T.g50, border: `1px solid ${T.g100}` }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: T.g400 }} />
            <span style={{ fontFamily: T.sans, fontSize: 14, fontWeight: 600, color: T.g700 }}>
              {ROLES.length} open roles across {[...new Set(ROLES.map(r => r.dept))].length} teams
            </span>
            <span style={{ fontFamily: T.sans, fontSize: 13, color: T.g600 }}>-- Nairobi, Mombasa, Kisumu</span>
          </div>
        </div>
      </div>

      {/* ══ WHAT WE LOOK FOR ══ */}
      <div style={{ borderBottom: `1px solid ${T.n100}`, padding: "0 20px" }}>
        <div style={{ maxWidth: 720, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
          {[
            { label: "You care about the problem", sub: "Not just the opportunity or the CV line" },
            { label: "You are honest about what you do not know", sub: "We value that more than confidence without basis" },
            { label: "You can work without a lot of structure", sub: "We are early. Ambiguity is part of the job" },
          ].map(({ label, sub }, i) => (
            <div key={i} style={{ padding: "22px 20px", display: "flex", alignItems: "flex-start", gap: 12, borderRight: i < 2 ? `1px solid ${T.n100}` : "none" }}>
              <Icon name="CheckCircle" size={16} color={T.g500} strokeWidth={2} style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.n800, marginBottom: 3 }}>{label}</div>
                <div style={{ fontSize: 12, color: T.n400, lineHeight: 1.5 }}>{sub}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ maxWidth: 720, margin: "0 auto", padding: "0 20px clamp(80px,10vw,120px)" }}>

        {/* ══ OPEN ROLES ══ */}
        <div style={{ paddingTop: "clamp(48px,7vw,64px)", marginBottom: "clamp(52px,7vw,72px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 12px" }}>Open roles</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,38px)", fontWeight: 400, color: T.n900, margin: "0 0 28px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            Where we need <em style={{ fontStyle: "italic", color: T.g600 }}>people right now</em>
          </h2>

          {/* Dept filter */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 28 }}>
            {ALL_DEPTS.map(dept => {
              const active = activeDept === dept;
              return (
                <button key={dept} onClick={() => setActiveDept(dept)} style={{
                  padding: "8px 16px", borderRadius: 99, fontFamily: T.sans,
                  border: `1.5px solid ${active ? T.g500 : T.n150}`,
                  background: active ? T.g500 : T.white,
                  color: active ? T.white : T.n600,
                  fontSize: 13, fontWeight: active ? 600 : 400,
                  cursor: "pointer", transition: "all .15s",
                }}>
                  {dept}
                  <span style={{ marginLeft: 6, fontSize: 11, color: active ? "rgba(255,255,255,.7)" : T.n300 }}>
                    {dept === "All" ? ROLES.length : ROLES.filter(r => r.dept === dept).length}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Role list */}
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {filtered.map(role => (
              <RoleCard key={role.id} role={role} />
            ))}
          </div>
        </div>

        {/* ══ WHAT IT IS LIKE HERE ══ */}
        <div style={{ marginBottom: "clamp(52px,7vw,72px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 12px" }}>What to expect</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,38px)", fontWeight: 400, color: T.n900, margin: "0 0 8px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            What working here <em style={{ fontStyle: "italic", color: T.g600 }}>actually means</em>
          </h2>
          <p style={{ fontFamily: T.sans, fontSize: 15, color: T.n400, margin: "0 0 8px", lineHeight: 1.65 }}>
            We are a startup. We will be honest about what that is and what it is not.
          </p>
          <div style={{ marginTop: 8 }}>
            {PERKS.map((p, i) => <PerkCard key={i} {...p} />)}
          </div>
        </div>

        {/* ══ HONEST ABOUT STAGE ══ */}
        <div style={{ marginBottom: "clamp(52px,7vw,72px)", padding: "clamp(28px,5vw,40px)", borderRadius: 18, background: T.n50, border: `1px solid ${T.n150}` }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 14px" }}>Honest section</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(22px,3.5vw,30px)", fontWeight: 400, color: T.n900, margin: "0 0 16px", letterSpacing: "-0.03em", lineHeight: 1.2 }}>
            Things we will not pretend<br/>
            <em style={{ fontStyle: "italic", color: T.g600 }}>are better than they are</em>
          </h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            {[
              { label: "We are early and things change fast", body: "The roadmap shifts. Priorities change. What you are working on in month one may not be what you are working on in month three. If you need stability and a clear path, this may not be the right time to join." },
              { label: "We do not have unlimited resources", body: "We are not a well-funded scale-up. We make tradeoffs. We are careful with money because we have to be. The upside is that every resource allocation decision involves the whole team." },
              { label: "The work is hard in a real way", body: "We are dealing with identity fraud, payment disputes, informal sector dynamics, and mobile internet constraints all at once. It is genuinely complex work. The challenge is the point, but it is still a challenge." },
              { label: "Your role will change as we grow", body: "If you join as the first product manager, you will not always be the only product manager. We will hire around you. The scope of your role will evolve, and we will be direct about what that looks like." },
            ].map((item, i) => (
              <div key={i} style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                <div style={{ width: 20, height: 20, borderRadius: "50%", background: T.white, border: `1.5px solid ${T.n200}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: T.n400 }}>{i + 1}</span>
                </div>
                <div>
                  <div style={{ fontFamily: T.sans, fontSize: 14, fontWeight: 600, color: T.n800, marginBottom: 5 }}>{item.label}</div>
                  <p style={{ fontFamily: T.sans, fontSize: 14, color: T.n600, lineHeight: 1.75, margin: 0 }}>{item.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ══ GENERAL APPLICATIONS ══ */}
        <div style={{ marginBottom: "clamp(52px,7vw,72px)", padding: "clamp(28px,5vw,40px)", borderRadius: 18, border: `1.5px solid ${T.g100}`, background: T.g50 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: T.white, border: `1.5px solid ${T.g200}`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 18 }}>
            <Icon name="Megaphone" size={20} color={T.g500} strokeWidth={1.7} />
          </div>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(22px,3.5vw,30px)", fontWeight: 400, color: T.n900, margin: "0 0 12px", letterSpacing: "-0.03em", lineHeight: 1.2 }}>
            Do not see your role?<br/>
            <em style={{ fontStyle: "italic", color: T.g600 }}>Reach out anyway</em>
          </h2>
          <p style={{ fontFamily: T.sans, fontSize: 15, color: T.n600, lineHeight: 1.75, margin: "0 0 24px", maxWidth: 460 }}>
            If you believe in what we are building and you have skills that are relevant -- in legal, finance, design, marketing, logistics, or anything else -- send us a short email explaining who you are and what you could contribute. We read every one.
          </p>
          <a
            href="mailto:jobs@jiraniapp.co.ke?subject=General%20Application&body=Hi%20Jirani%20team%2C%0A%0AI%20am%20reaching%20out%20with%20a%20general%20application.%0A%0A"
            style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              background: T.g500, color: T.white, border: "none",
              padding: "13px 26px", borderRadius: 10, fontSize: 14, fontWeight: 600,
              cursor: "pointer", fontFamily: T.sans, letterSpacing: "0.01em",
              textDecoration: "none",
            }}
            onMouseEnter={e => e.currentTarget.style.background = T.g600}
            onMouseLeave={e => e.currentTarget.style.background = T.g500}>
            Send a general application
            <Icon name="ArrowRight" size={15} color={T.white} strokeWidth={2} />
          </a>
          <div style={{ marginTop: 14, fontSize: 13, color: T.g600 }}>
            jobs@jiraniapp.co.ke
          </div>
        </div>

        {/* ══ FOOTER CTA ══ */}
        <div style={{ padding: "clamp(36px,5vw,52px)", borderRadius: 20, border: `1px solid ${T.n150}`, textAlign: "center", background: T.n50 }}>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(24px,4vw,36px)", fontWeight: 400, color: T.n900, margin: "0 0 10px", letterSpacing: "-0.03em", lineHeight: 1.2 }}>
            Not ready to apply but<br/>
            <em style={{ fontStyle: "italic", color: T.g600 }}>want to follow along?</em>
          </h2>
          <p style={{ fontFamily: T.sans, fontSize: 15, color: T.n400, margin: "0 auto 28px", lineHeight: 1.75, maxWidth: 400 }}>
            See the product you would be building. Browse JiraniApp as a client or check out the professional experience.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={() => navigate("/search")} style={{
              padding: "13px 26px", background: T.g500, color: T.white,
              border: "none", borderRadius: 11, fontSize: 14, fontWeight: 600,
              fontFamily: T.sans, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8,
            }}
              onMouseEnter={e => e.currentTarget.style.background = T.g600}
              onMouseLeave={e => e.currentTarget.style.background = T.g500}>
              See the product
              <Icon name="ArrowRight" size={15} color={T.white} strokeWidth={2} />
            </button>
            <button onClick={() => navigate("/about")} style={{
              padding: "13px 26px", background: "transparent", color: T.n700,
              border: `1.5px solid ${T.n200}`, borderRadius: 11, fontSize: 14, fontWeight: 600,
              fontFamily: T.sans, cursor: "pointer",
            }}
              onMouseEnter={e => e.currentTarget.style.borderColor = T.n400}
              onMouseLeave={e => e.currentTarget.style.borderColor = T.n200}>
              Read our story
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}